/**
 * App-wide theme (light/dark).
 *
 * The source of truth is `data-ajo-theme` on <html>; every surface styles
 * itself through the CSS tokens in app-shell.css, which are re-declared
 * under `html[data-ajo-theme='dark']`. Persisted in localStorage and
 * applied before React mounts (main.tsx imports this module for the
 * side-effect) so there's no light→dark flash.
 */
import * as React from 'react';

const KEY = 'ajo:theme';
const LEGACY_KEY = 'ajo:ud-theme'; // pre-existing dashboard-only toggle

export type AjoTheme = 'light' | 'dark';

function readStored(): AjoTheme {
  try {
    const v = localStorage.getItem(KEY) || localStorage.getItem(LEGACY_KEY);
    return v === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

let current: AjoTheme = readStored();
const listeners = new Set<(t: AjoTheme) => void>();

function apply(t: AjoTheme) {
  document.documentElement.dataset.ajoTheme = t;
}

export function getTheme(): AjoTheme {
  return current;
}

export function setTheme(t: AjoTheme) {
  current = t;
  try {
    localStorage.setItem(KEY, t);
    localStorage.setItem(LEGACY_KEY, t); // keep old key coherent
  } catch {}
  apply(t);
  listeners.forEach((fn) => fn(t));
}

export function toggleTheme() {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

/** React hook — re-renders the consumer when the theme flips. */
export function useAjoTheme(): AjoTheme {
  const [t, setT] = React.useState<AjoTheme>(current);
  React.useEffect(() => {
    listeners.add(setT);
    return () => {
      listeners.delete(setT);
    };
  }, []);
  return t;
}

// Apply persisted theme immediately on module load (pre-mount).
apply(current);
